import { useEffect, useState } from 'react'

import DeleteIcon from '@/assets/icons/delete.svg?react'
import { Button, Checkbox, Pagination, Trans } from '@/components'
import { EmptyView } from '@/components/EmptyView'
import { ClusterInstance } from '@/models/mesh'
import { Network } from '@/models/network'
import { cn } from '@/utils/style'

interface ClusterInstanceTargetTableProps {
  network: Network
  sourceInstances: ClusterInstance[]
  onRemoveSelectedFromTarget: (removeKeys: React.Key[]) => void
}

export default function ClusterInstanceTargetTable({
  network,
  sourceInstances,
  onRemoveSelectedFromTarget,
}: ClusterInstanceTargetTableProps) {
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([])
  const [instaceList, setInstanceList] = useState<ClusterInstance[]>([])

  const pageSize = 4

  const fetchData = async (p = page) => {
    try {
      const targetInstances = sourceInstances?.filter((instance) => instance.disabled)
      setPage(p)
      setTotal(targetInstances?.length ?? 0)

      const paginatedData = targetInstances?.slice((p - 1) * pageSize, p * pageSize)
      setInstanceList(paginatedData)
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    fetchData(1)
  }, [network])

  useEffect(() => {
    fetchData(page)
  }, [sourceInstances])

  const handleRemoveSelectedFromTarget = () => {
    onRemoveSelectedFromTarget(selectedKeys)
    setSelectedKeys([])
  }

  const handleRowSelection = (item: ClusterInstance, checked: boolean) => {
    let updatedRows = []
    if (checked) {
      updatedRows = [...selectedKeys, item.resID]
    } else {
      updatedRows = selectedKeys.filter((key) => key !== item.resID)
    }
    setSelectedKeys(updatedRows)
  }

  return (
    <div className="rounded-2xl border border-neutral-200 p-4">
      <div className="min-h-[32px] flex items-center justify-between">
        <div className="t-sb">
          <Trans i18nKey="mesh.type.networkGroup">Network Group</Trans> - {network.name}
        </div>
        {(selectedKeys?.length ?? 0) > 0 ? (
          <Button
            type="danger"
            size="sm"
            className={cn('bg-red-50 text-red-500')}
            onClick={handleRemoveSelectedFromTarget}
          >
            Remove Selected
          </Button>
        ) : null}
      </div>

      <div className="h-[396px] mt-2">
        <table className="table table-fixed w-full">
          <thead>
            <tr>
              <th className="w-auto rounded-l-md"></th>
              <th className="w-[32%]">
                <Trans i18nKey="mesh.clusterAddInsModal.tableHeaderID">ID</Trans>
              </th>
              <th className="w-[44%]">
                <Trans i18nKey="mesh.clusterAddInsModal.tableHeaderName">Name</Trans>
              </th>
              <th className="w-[16%] rounded-r-md">
                <Trans i18nKey="mesh.clusterAddInsModal.tableHeaderAction">Action</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {instaceList.length > 0 &&
              instaceList.map((data, idx) => (
                <tr key={`${data.resID}${idx}`} className="h-[72px]">
                  <td className="w-auto">
                    <Checkbox onChange={(checked) => handleRowSelection(data, checked)} />
                  </td>
                  <td className="w-[32%] t-sm">{data?.resID ?? '-'}</td>
                  <td className="w-[44%] t-sm">{data?.gpuName ?? data.cpuName ?? '-'}</td>
                  <td className="w-[16%]">
                    <DeleteIcon
                      className="w-5 h-5 cursor-pointer text-tertiary"
                      onClick={() => {
                        onRemoveSelectedFromTarget([data.resID])
                        handleRowSelection(data, false)
                      }}
                    />
                  </td>
                </tr>
              ))}
            {instaceList.length === 0 && (
              <tr>
                <td colSpan={4} className="border-none">
                  <EmptyView
                    content={
                      <span className="t-sm text-secondary">
                        <Trans i18nKey="common.emptyViewNoDataMsg">No Data</Trans>
                      </span>
                    }
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {total > 0 && (
          <Pagination
            size={pageSize}
            total={total}
            current={page}
            className="justify-end mt-4"
            onChange={(page) => fetchData(page)}
          />
        )}
      </div>
    </div>
  )
}
